import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { I18nProvider } from "@/i18n/I18nContext";
import { LocationScopeProvider } from "@/lib/locationScope";
import RequireAuth from "@/auth/RequireAuth";
import DesktopOnly from "@/auth/DesktopOnly";
import AppShell from "@/components/AppShell";
import AuthCallback from "@/auth/AuthCallback";

import LoginPage from "@/pages/Login";
import CreateCompany from "@/pages/CreateCompany";
import JoinCompany from "@/pages/JoinCompany";
import VerifyEmail from "@/pages/VerifyEmail";
import Settings from "@/pages/Settings";
import { PrivacyPage, TermsPage } from "@/pages/LegalPages";
import PauseSettings from "@/pages/PauseSettings";
import News from "@/pages/News";
import Preferences from "@/pages/Preferences";
import Dashboard from "@/pages/Dashboard";
import Roster from "@/pages/Roster";
import Employees from "@/pages/Employees";
import Documents from "@/pages/Documents";
import Locations from "@/pages/Locations";
import Teams from "@/pages/Teams";
import LeaveRequests from "@/pages/LeaveRequests";
import Swaps from "@/pages/Swaps";
import Timesheets from "@/pages/Timesheets";
import Reports from "@/pages/Reports";
import Payroll from "@/pages/Payroll";
import MyOverview from "@/pages/MyOverview";
import Notifications from "@/pages/Notifications";
import MyRoster from "@/pages/MyRoster";
import TeamToday from "@/pages/TeamToday";
import QuickAssign from "@/pages/QuickAssign";
import ManagerRequests from "@/pages/ManagerRequests";
import MyLeave from "@/pages/MyLeave";
import MySwaps from "@/pages/MySwaps";
import MyAvailability from "@/pages/MyAvailability";
import Profile from "@/pages/Profile";
import Handbook from "@/pages/Handbook";
import Billing, { BillingSuccess, BillingCancel } from "@/pages/Billing";
import Permissions from "@/pages/Permissions";
import Welcome from "@/pages/Welcome";

function RoleLanding() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return (
    <Navigate
      to={["owner", "manager", "teamlead"].includes(user.role) ? "/dashboard" : "/my-roster"}
      replace
    />
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <LocationScopeProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              style: {
                borderRadius: 14,
                fontFamily: "IBM Plex Sans, sans-serif",
                fontSize: "13px",
                fontWeight: 500,
                padding: "12px 14px",
                boxShadow: "0 12px 40px -8px rgb(0 0 0 / 0.18), 0 2px 6px -2px rgb(0 0 0 / 0.06)",
              },
              className: "rostr-toast",
            }}
          />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/create-company" element={<CreateCompany />} />
            <Route path="/join" element={<JoinCompany />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/welcome" element={<RequireAuth roles={["owner"]}><Welcome /></RequireAuth>} />
            <Route path="/" element={<RoleLanding />} />
            <Route element={<RequireAuth><AppShell /></RequireAuth>}>
              {/* Manager — desktop-only */}
              <Route path="/dashboard" element={<RequireAuth roles={["owner", "manager", "teamlead"]}><DesktopOnly><Dashboard /></DesktopOnly></RequireAuth>} />
              <Route path="/roster" element={<RequireAuth roles={["owner", "manager", "teamlead"]}><Roster /></RequireAuth>} />
              <Route path="/employees" element={<RequireAuth roles={["owner", "manager", "teamlead"]}><DesktopOnly><Employees /></DesktopOnly></RequireAuth>} />
              <Route path="/locations" element={<RequireAuth roles={["owner"]}><DesktopOnly><Locations /></DesktopOnly></RequireAuth>} />
              <Route path="/teams" element={<RequireAuth roles={["owner", "manager", "teamlead"]}><DesktopOnly><Teams /></DesktopOnly></RequireAuth>} />
              <Route path="/permissions" element={<RequireAuth roles={["owner", "manager"]}><DesktopOnly><Permissions /></DesktopOnly></RequireAuth>} />
              <Route path="/requests" element={<RequireAuth roles={["owner", "manager", "teamlead"]}><DesktopOnly><LeaveRequests /></DesktopOnly></RequireAuth>} />
              <Route path="/swaps" element={<RequireAuth roles={["owner", "manager", "teamlead"]}><DesktopOnly><Swaps /></DesktopOnly></RequireAuth>} />
              <Route path="/timesheets" element={<RequireAuth roles={["owner", "manager", "teamlead"]}><DesktopOnly><Timesheets /></DesktopOnly></RequireAuth>} />
              <Route path="/reports" element={<RequireAuth roles={["owner", "manager", "teamlead"]}><DesktopOnly><Reports /></DesktopOnly></RequireAuth>} />
              <Route path="/payroll" element={<RequireAuth roles={["owner", "manager"]}><DesktopOnly><Payroll /></DesktopOnly></RequireAuth>} />
              <Route path="/documents" element={<RequireAuth><Documents /></RequireAuth>} />
              <Route path="/settings" element={<RequireAuth roles={["owner"]}><DesktopOnly><Settings /></DesktopOnly></RequireAuth>} />
              <Route path="/pause-settings" element={<RequireAuth roles={["owner", "manager"]}><DesktopOnly><PauseSettings /></DesktopOnly></RequireAuth>} />
              {/* Shared */}
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/news" element={<News />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/preferences" element={<Preferences />} />
              <Route path="/handbook" element={<Handbook />} />
              <Route path="/billing" element={<RequireAuth roles={["owner"]}><Billing /></RequireAuth>} />
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/billing/cancel" element={<BillingCancel />} />
              {/* Employee + shared mobile pages */}
              <Route path="/team-today" element={<RequireAuth roles={["owner", "manager", "teamlead"]}><Navigate to="/roster?view=team-today" replace /></RequireAuth>} />
              <Route path="/quick-assign" element={<RequireAuth roles={["owner", "manager", "teamlead"]}><Navigate to="/roster?view=open-shifts" replace /></RequireAuth>} />
              <Route path="/manager-requests" element={<RequireAuth roles={["owner", "manager", "teamlead"]}><ManagerRequests /></RequireAuth>} />
              <Route path="/my-roster" element={<MyRoster />} />
              <Route path="/my-overview" element={<MyOverview />} />
              <Route path="/my-leave" element={<MyLeave />} />
              <Route path="/my-swaps" element={<MySwaps />} />
              <Route path="/my-availability" element={<MyAvailability />} />
            </Route>
            {/* Fase 6 — public legal pages (no auth) */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </LocationScopeProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
